/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  ModalCard,
  ModalCloseButton,
  ModalShell,
} from '@/Client/Widgets';

import {
  allBatchRowsTerminal,
  canSubmitBatch,
  getPickerFooterHint,
  getValidatedFiles,
  hasPendingUploads,
  isBatchActive,
} from '@kaiizen/kai-pro-api';
import { AIScanVideoUploadDropzone } from './AIScanVideoUploadDropzone';
import { AIScanVideoUploadFileList } from './AIScanVideoUploadFileList';
import {
  type AIScanVideoUploadModalVariant,
  SCAN_VIDEO_UPLOAD_MODAL_COPY,
} from './aiScanVideoUploadModalCopy';
import {
  EYEBROW_STYLE,
  FOOT_ACTIONS_STYLE,
  FOOT_HINT_STYLE,
  FOOT_WITH_HINT_STYLE,
  footHintErrorStyle,
  HEAD_STYLE,
  SUBTITLE_STYLE,
  TITLE_STYLE,
} from './aiScanVideoUploadModalStyles';
import {
  type AIScanVideoActiveWorkSnapshot,
  useAIScanVideoActiveWork,
} from './useAIScanVideoActiveWork';
import { useAIScanVideoFilePicker } from './useAIScanVideoFilePicker';
import { type BatchUploadHandlers, useAIScanVideoUpload } from './useAIScanVideoUpload';

type AIScanVideoUploadModalProps = {
  visible: boolean;
  onClose: () => void;
  scanId: string;
  /** `origin` = empty draft (Step 1); `add` = populated scope (Step 2+). */
  variant?: AIScanVideoUploadModalVariant;
  /** Reuse a parent-mounted poll (scan page / project card) instead of starting a second one. */
  serverActiveWork?: AIScanVideoActiveWorkSnapshot;
};

export const AIScanVideoUploadModal = ({
  visible,
  onClose,
  scanId,
  variant = 'origin',
  serverActiveWork,
}: AIScanVideoUploadModalProps) => {
  const [pollUploadWhileClosed, setPollUploadWhileClosed] = useState(false);

  const {
    analysisError,
    videoProgressById,
    submitBatch,
    retryAnalysis,
    reset,
    resumeFromServerItems,
    isUploading,
    isBatchActive: hookBatchActive,
  } = useAIScanVideoUpload(scanId, visible || pollUploadWhileClosed);

  const internalActiveWork = useAIScanVideoActiveWork(
    scanId,
    visible && serverActiveWork === undefined,
  );
  const activeWorkItems = serverActiveWork?.items ?? internalActiveWork.items;
  const activeWorkFetched = serverActiveWork?.hasFetchedOnce ?? internalActiveWork.hasFetchedOnce;

  const batchHandlersRef = useRef<BatchUploadHandlers>({});

  const {
    selectedFiles,
    overflowNotice,
    acceptFiles,
    removeFile,
    clearSelection,
    hydrateFromActiveWorkItems,
    markUploading,
    setUploadProgress,
    markFileBad,
    markUploaded,
    markAnalyzing,
    syncJobProgress,
  } = useAIScanVideoFilePicker();

  batchHandlersRef.current = {
    onFileUploadProgress: setUploadProgress,
    onFileUploadFailed: markFileBad,
    onFileUploadComplete: (fileId, videoId) => {
      markUploaded(fileId, videoId);
    },
    onAnalyzeStarted: (fileId, videoId) => {
      markAnalyzing(fileId, videoId);
    },
    onJobProgress: (fileId, polled) => {
      syncJobProgress(fileId, polled);
    },
  };

  const batchActive = isBatchActive(selectedFiles) || hookBatchActive;
  const showDone = allBatchRowsTerminal(selectedFiles);
  const canClose = !hasPendingUploads(selectedFiles) && !isUploading;
  const canAnalyze = canSubmitBatch(selectedFiles) && !isUploading;
  const canRetryAnalysis = analysisError != null && !isUploading;
  const footerHint = analysisError
    ? { text: analysisError, isError: true }
    : getPickerFooterHint(selectedFiles, overflowNotice);

  const hydratedFromServerRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      hydratedFromServerRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (!hookBatchActive && !visible) {
      setPollUploadWhileClosed(false);
    }
  }, [hookBatchActive, visible]);

  useEffect(() => {
    if (!visible || !activeWorkFetched) return;
    if (hookBatchActive || isBatchActive(selectedFiles)) return;

    if (activeWorkItems.length > 0) {
      if (hydratedFromServerRef.current) return;
      hydratedFromServerRef.current = true;

      hydrateFromActiveWorkItems(activeWorkItems);
      resumeFromServerItems(activeWorkItems, batchHandlersRef.current);
    }
  }, [
    activeWorkFetched,
    activeWorkItems,
    hookBatchActive,
    hydrateFromActiveWorkItems,
    resumeFromServerItems,
    selectedFiles,
    visible,
  ]);

  const handleClose = () => {
    if (!canClose && !showDone) return;

    const preserveInFlightBatch = (isBatchActive(selectedFiles) || hookBatchActive) && !showDone;
    if (preserveInFlightBatch) {
      setPollUploadWhileClosed(true);
    } else {
      setPollUploadWhileClosed(false);
      clearSelection();
      reset();
    }
    onClose();
  };

  const handleUpload = () => {
    const uploadItems = getValidatedFiles(selectedFiles).flatMap(row =>
      row.file instanceof File
        ? [{
          fileId: row.id,
          file: row.file,
          durationSeconds: row.durationSeconds,
          width: row.width,
          height: row.height,
          videoRecordedAt: row.videoRecordedAt,
        }]
        : []
    );
    if (!canAnalyze || !uploadItems.length) return;

    markUploading(uploadItems.map(item => item.fileId));
    void submitBatch(uploadItems, batchHandlersRef.current);
  };

  const handleRetryAnalysis = () => {
    if (!canRetryAnalysis) return;
    void retryAnalysis();
  };

  const copy = SCAN_VIDEO_UPLOAD_MODAL_COPY[variant];

  return (
    <ModalShell visible={visible} onClose={handleClose} contentMaxWidth='560px'>
      <ModalCard>
        <ModalCloseButton onClick={handleClose} disabled={!canClose && !showDone} data-testid='ai-scan-video-close' />

        <div css={HEAD_STYLE}>
          <span css={EYEBROW_STYLE}>{copy.eyebrow}</span>
          <div css={TITLE_STYLE}>{copy.title}</div>
          <div css={SUBTITLE_STYLE}>{copy.subtitle}</div>
        </div>

        <AIScanVideoUploadDropzone
          disabled={batchActive}
          fileCount={selectedFiles.length}
          onSelectFiles={acceptFiles}
        />

        <AIScanVideoUploadFileList
          files={selectedFiles}
          videoProgressById={videoProgressById}
          onRemove={removeFile}
        />

        <div css={FOOT_WITH_HINT_STYLE}>
          {footerHint.text
            ? (
              <span css={[FOOT_HINT_STYLE, footerHint.isError && footHintErrorStyle]}>
                {footerHint.text}
              </span>
            )
            : <span css={FOOT_HINT_STYLE} aria-hidden={'true'} />}
          <div css={FOOT_ACTIONS_STYLE}>
            {!showDone && !canRetryAnalysis && (
              <Button buttonStyle='gray' onClick={handleClose} disabled={!canClose}>
                Cancel
              </Button>
            )}
            {showDone
              ? (
                <Button buttonStyle='yellow' onClick={handleClose}>
                  Done
                </Button>
              )
              : canRetryAnalysis
              ? (
                <Button buttonStyle='yellow' onClick={handleRetryAnalysis}>
                  Try again
                </Button>
              )
              : (
                <Button
                  buttonStyle='yellow'
                  disabled={!canAnalyze}
                  onClick={handleUpload}
                >
                  {isUploading ? 'Uploading…' : 'Upload →'}
                </Button>
              )}
          </div>
        </div>
      </ModalCard>
    </ModalShell>
  );
};
